import type { AccountProfile, AdminProfile } from '@/types/iam.types';
import type { UserInfo } from '@/types/auth.types';
import type { User } from '@/types';
import { normalizeAuthUserType } from '@/types/auth.types';

function domainAliasFromProfile(profile: AccountProfile): string {
  return profile.domain?.domain_alias ?? profile.domain?.name ?? '';
}

/**
 * 将 Valhalla GetAccountProfile 映射为前端登录态 UserInfo
 */
export function accountProfileToUserInfo(
  profile: AccountProfile,
  fallbackUsername: string
): UserInfo {
  const isIamUser = profile.user != null;
  const userType = normalizeAuthUserType(isIamUser ? 'user' : 'account', 'account');
  const uid =
    (isIamUser ? profile.user?.id : profile.account?.id) ??
    profile.account?.id ??
    profile.user?.id ??
    '';
  return {
    user_id: uid,
    username: (isIamUser ? profile.user?.name : profile.account?.name) ?? fallbackUsername,
    user_type: userType,
    account_id: profile.account?.id ?? '',
    iam_user_id: profile.user?.id ?? '',
    domain_id: profile.domain?.id ?? '',
    domain_alias: domainAliasFromProfile(profile),
    account: profile.account as Record<string, unknown> | undefined,
    user: profile.user as Record<string, unknown> | undefined,
    domain: profile.domain as Record<string, unknown> | undefined,
  };
}

/**
 * 将 Valhalla profile 映射为「用户管理」页使用的简化 User（原来自 agent-platform /users API）
 */
export function accountProfileToUser(profile: AccountProfile): User {
  const isIamUser = profile.user != null;
  const uid =
    (isIamUser ? profile.user?.id : profile.account?.id) ??
    profile.account?.id ??
    profile.user?.id ??
    '';
  return {
    uid,
    name: (isIamUser ? profile.user?.name : profile.account?.name) ?? '',
    user_type: isIamUser ? 'user' : 'account',
    account_id: profile.account?.id ?? '',
    iam_user_id: profile.user?.id ?? '',
    domain_id: profile.domain?.id ?? '',
    domain_alias: domainAliasFromProfile(profile),
    preferences: '',
  };
}

/**
 * 将 Valhalla GetAdminProfile 映射为前端登录态 UserInfo
 */
export function adminProfileToUserInfo(profile: AdminProfile, fallbackUsername: string): UserInfo {
  return {
    user_id: profile.id,
    username: profile.name || fallbackUsername,
    user_type: 'admin',
    account_id: profile.id,
    iam_user_id: profile.id,
    domain_id: '',
    domain_alias: '',
  };
}

export function iamUserToUser(u: { id: string; name: string; domain_id?: string }): User {
  return {
    uid: u.id,
    name: u.name,
    user_type: 'user',
    account_id: '',
    iam_user_id: u.id,
    domain_id: u.domain_id ?? '',
    domain_alias: '',
    preferences: '',
  };
}
